import type { Transaction } from "@rocicorp/zero";
import { type Context, userFacingError } from "../authorization";
import { dropUsagesLostByGrantRemoval } from "../connectorUsages";
import type { Schema } from "../generatedDrizzleSchema";
import { assertNotPersonal } from "../spaceName";
import { fetchShareableScope } from "./grantResources";
import { grantsForScope } from "./grantQueries";
import type { ShareableScopeKind } from "./permissions";
import { checkServiceAccountGrant } from "./serviceAccountGrantAuthorization";
import { type GrantRemoval, resolveGrantDelta } from "./spaceGrantDelta";
import {
  applySpaceSupplierAutoGrant,
  type SupplierFlag,
} from "./spaceSupplierAutoGrant";
import {
  applyGrantDiff,
  assertDependentPrincipalsMeetRequirements,
  type GrantInput,
  principalsCrossingIntoRequirement,
  type ResolvedCustomRole,
  validateGrants,
} from "./validation";

// Applies add/remove deltas to a scope's grants in one transaction, validating
// and escalation-checking ONLY the insertions — a pre-existing grant is never
// re-authorized. Generic across every shareable scope kind; the scope-specific
// behavior is confined to the branches below:
//   space         → the space_uses_* crossing guard (+ optional supplier auto-grant)
//   connected_app → dropUsagesLostByGrantRemoval on removed grants
//   skill/network/service_account → neither
export async function applyScopeGrantPatch(opts: {
  tx: Transaction<Schema>;
  ctx: Context;
  scopeId: string;
  scopeKind: ShareableScopeKind;
  add: readonly GrantInput[];
  remove: readonly GrantRemoval[];
  customRoles: readonly ResolvedCustomRole[];
  autoGrant?: Record<SupplierFlag, boolean>;
}): Promise<void> {
  const { tx, ctx, scopeId, scopeKind, add, remove, customRoles } = opts;

  // Fetch inside the space branch so scopeKind narrows to "space" and the row
  // type carries `mode`; the else still fetches to validate existence/tenant.
  if (scopeKind === "space") {
    const space = await fetchShareableScope(
      tx,
      scopeId,
      scopeKind,
      ctx.tenantId
    );
    assertNotPersonal(space, "set grants on");
  } else {
    await fetchShareableScope(tx, scopeId, scopeKind, ctx.tenantId);
  }

  const allExisting = await tx.run(grantsForScope(scopeId, ctx.tenantId));
  const existing = allExisting.filter(
    (g) => g.userGroupId || g.userId || g.serviceAccountId
  );

  const { toInsert, toDelete } = resolveGrantDelta(existing, add, remove);

  // Validate + escalation-guard the insertions only.
  const resolvedInserts = validateGrants(toInsert, scopeKind, customRoles);
  for (const { input, role } of resolvedInserts) {
    if (!input.serviceAccountId) continue;
    const denial = await checkServiceAccountGrant({ tx, ctx, scopeId, role });
    if (denial?.reason === "allow_all")
      throw userFacingError(
        `You can’t grant a service account a role carrying "allow_all"`,
        { forbidden: true }
      );
    if (denial?.reason === "missing_permission")
      throw userFacingError(
        `You can’t grant "${denial.permission}" to a service account: you lack it on this resource`,
        { forbidden: true }
      );
  }

  const deleteIds = new Set(toDelete.map((g) => g.id));
  const desired = [
    ...existing.filter((g) => !deleteIds.has(g.id)),
    ...resolvedInserts.map(({ input, role }) => ({
      userGroupId: input.userGroupId ?? null,
      userId: input.userId ?? null,
      serviceAccountId: input.serviceAccountId ?? null,
      builtinRoleId: role.builtinRoleId,
      customRole: role.customRole,
    })),
  ];

  // Space is the only cross-resource dependent: adding a member may require
  // supplier access. Auto-grant provisions it (when requested); the guard then
  // blocks any crossing principal still lacking it. Skip it entirely when no
  // flag is set, so a plain space patch doesn't do the membership-resolution
  // work for a guaranteed no-op.
  if (
    scopeKind === "space" &&
    (opts.autoGrant?.connectors || opts.autoGrant?.skills)
  ) {
    await applySpaceSupplierAutoGrant({
      tx,
      ctx,
      spaceId: scopeId,
      existingSpaceGrants: existing,
      desiredSpaceGrants: desired,
      flags: opts.autoGrant,
    });
  }

  if (scopeKind === "space") {
    for (const ruleId of [
      "space_uses_connected_apps",
      "space_uses_skills",
    ] as const) {
      const elevated = principalsCrossingIntoRequirement(
        ruleId,
        existing,
        desired
      );
      await assertDependentPrincipalsMeetRequirements(
        tx,
        ctx.tenantId,
        ruleId,
        scopeId,
        elevated
      );
    }
  }

  await applyGrantDiff(tx, scopeId, { toInsert, toDelete }, ctx.tenantId);

  // Connectors are suppliers: revoking a grant may strip a dependent's access,
  // so disconnect the usages that relied on it. Other kinds need no removal
  // handling.
  if (scopeKind === "connected_app") {
    const removedGroupIds: string[] = [];
    const removedUserIds: string[] = [];
    const removedServiceAccountIds: string[] = [];
    for (const g of toDelete) {
      if (g.userGroupId) removedGroupIds.push(g.userGroupId);
      if (g.userId) removedUserIds.push(g.userId);
      if (g.serviceAccountId) removedServiceAccountIds.push(g.serviceAccountId);
    }
    await dropUsagesLostByGrantRemoval(
      tx,
      ctx.tenantId,
      scopeId,
      removedGroupIds,
      removedUserIds,
      removedServiceAccountIds
    );
  }
}
